/**
 * Wave 8 — deterministic revision workspace intelligence (FE mirror of policy; not AI).
 */
import type { Quotation } from '@/services/quotation.service';
import type { WorkflowStage } from '@/components/enterprise';
import {
  canConvertQuotationToSalesOrder,
  canCreateQuotationRevision,
  canSendQuotationToCustomer,
  displayQuotationRevisionLabel,
} from './quotationRevisionPolicy';

export type QuotationRevisionInsight = {
  severity: 'info' | 'warning' | 'success';
  message: string;
  actionKey?: 'edit' | 'submit_approval' | 'send' | 'accept' | 'revise' | 'convert' | 'open_current';
};

function norm(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

export function resolveFamilyRole(
  row: Pick<
    Quotation,
    'id' | 'status' | 'is_current_submitted' | 'is_latest_revision' | 'superseded_at' | 'revision_number'
  >
): 'current_offer' | 'accepted' | 'superseded' | 'draft_tip' | 'historical' {
  const status = norm(row.status);
  if (row.is_current_submitted && status === 'sent') return 'current_offer';
  if (row.is_current_submitted && status === 'accepted') return 'accepted';
  if (status === 'accepted') return 'accepted';
  if (status === 'revised' || row.superseded_at) return 'superseded';
  if (status === 'draft' && row.is_latest_revision) return 'draft_tip';
  return 'historical';
}

export function buildRevisionWorkflowStages(
  quotation: Pick<Quotation, 'status' | 'approval_status' | 'is_converted_to_sales_order'>
): WorkflowStage[] {
  const status = norm(quotation.status);
  const approval = norm(quotation.approval_status);
  const converted = Boolean(quotation.is_converted_to_sales_order);

  const approvalDone = approval === 'approved' || ['sent', 'accepted', 'revised'].includes(status);
  const sentDone = ['sent', 'accepted'].includes(status);
  const acceptedDone = status === 'accepted';

  return [
    {
      title: 'Draft',
      status: status === 'draft' && !approvalDone ? 'process' : 'finish',
    },
    {
      title: 'Approved',
      status: approvalDone ? 'finish' : approval === 'rejected' ? 'error' : 'wait',
    },
    {
      title: 'Sent',
      status: sentDone ? 'finish' : approvalDone && status === 'draft' ? 'process' : 'wait',
    },
    {
      title: 'Accepted',
      status: acceptedDone ? 'finish' : sentDone ? 'process' : 'wait',
    },
    {
      title: 'Customer Order',
      status: converted ? 'finish' : acceptedDone ? 'process' : 'wait',
    },
  ];
}

export function buildRevisionWorkflowCurrentIndex(
  quotation: Pick<Quotation, 'status' | 'approval_status' | 'is_converted_to_sales_order'>
): number {
  const status = norm(quotation.status);
  if (quotation.is_converted_to_sales_order) return 4;
  if (status === 'accepted') return 3;
  if (status === 'sent') return 2;
  if (norm(quotation.approval_status) === 'approved' && status === 'draft') return 1;
  if (status === 'revised') return 2;
  return 0;
}

export function buildQuotationRevisionInsights(
  quotation: Quotation,
  family: Quotation[],
  opts?: { universalApprovalPending?: boolean }
): QuotationRevisionInsight[] {
  const insights: QuotationRevisionInsight[] = [];
  const status = norm(quotation.status);
  const approval = norm(quotation.approval_status);
  const role = resolveFamilyRole(quotation);
  const revLabel = displayQuotationRevisionLabel(quotation.revision_number);

  if (role === 'superseded') {
    const current = family.find((r) => r.is_current_submitted);
    insights.push({
      severity: 'warning',
      message: current
        ? `${revLabel} is superseded. Current customer offer is ${displayQuotationRevisionLabel(current.revision_number)} (${current.status}).`
        : `${revLabel} is superseded. Open the latest revision in this family.`,
      actionKey: current ? 'open_current' : undefined,
    });
    return insights;
  }

  if (status === 'draft' && approval === 'pending' && !quotation.approval_instance_id) {
    insights.push({
      severity: 'info',
      message: 'Complete internal approval before sending this revision to the customer.',
      actionKey: 'submit_approval',
    });
  }

  if (opts?.universalApprovalPending) {
    insights.push({
      severity: 'info',
      message: 'Universal approval is in progress for this revision.',
    });
  }

  const sendGate = canSendQuotationToCustomer(quotation);
  if (sendGate.ok) {
    insights.push({
      severity: 'success',
      message: `${revLabel} is approved and ready to send to the customer (will supersede prior Sent/Accepted offers).`,
      actionKey: 'send',
    });
  }

  if (status === 'sent' && quotation.is_current_submitted) {
    insights.push({
      severity: 'info',
      message: 'This is the current customer offer. Mark as Accepted when the customer confirms.',
      actionKey: 'accept',
    });
  }

  const reviseGate = canCreateQuotationRevision(quotation);
  if (reviseGate.ok) {
    insights.push({
      severity: 'info',
      message: 'Commercial changes require Create Revision — prior Sent offers stay effective until you send a newer revision.',
      actionKey: 'revise',
    });
  }

  const convertGate = canConvertQuotationToSalesOrder(quotation);
  if (convertGate.ok) {
    insights.push({
      severity: 'success',
      message: 'This accepted revision can be converted to a Customer Order (exact row freeze).',
      actionKey: 'convert',
    });
  } else if (status === 'accepted' && !convertGate.ok && convertGate.reason) {
    insights.push({ severity: 'warning', message: convertGate.reason });
  }

  if (family.length > 1 && role === 'draft_tip') {
    const current = family.find((r) => r.is_current_submitted);
    if (current && current.id !== quotation.id) {
      insights.push({
        severity: 'info',
        message: `Draft ${revLabel} does not supersede ${displayQuotationRevisionLabel(current.revision_number)} (${current.status}) until you send this revision.`,
      });
    }
  }

  return insights;
}
